export const arrivalNotificationNameLabels = [
    {
        key: 'ARRIVAL_NOTIFICATION',
        value: 'Arrival Notification (ICS2)',
    },
];

/**
 * The ICS2 IE3N06 Arrival Notification.
 *
 * Unlike every other message the app files, this is not a `Declaration`: the declarations service
 * owns it as an aggregate of its own, under `/eu/ics2/arrival-notifications`, with its own id,
 * version, status and notification history. It is never nested inside a declaration envelope.
 */

export interface ICS2ArrivalCommunication {
    identifier?: string;
    type?: string;
}

export interface ICS2ArrivalParty {
    identificationNumber?: string;
    communication?: ICS2ArrivalCommunication[];
}

export interface ICS2ArrivalActiveBorderTransportMeans {
    modeOfTransport?: string;
    identificationNumber?: string;
    typeOfIdentification?: string;
    conveyanceReferenceNumber?: string;
    actualDateAndTimeOfArrival?: string;
    estimatedDateAndTimeOfArrival?: string;
}

export interface ICS2ArrivalCustomsOffice {
    referenceNumber?: string;
}

export interface ICS2ArrivalData {
    reference?: string;
    activeBorderTransportMeans?: ICS2ArrivalActiveBorderTransportMeans;
    actualCustomsOfficeOfFirstEntry?: ICS2ArrivalCustomsOffice;
    personNotifyingTheArrival?: ICS2ArrivalParty;
    notifyParties?: ICS2ArrivalParty[];
    relatedMrns?: string[];
}

export type ICS2ArrivalStatus = 'DRAFT' | 'SUBMITTED' | 'REGISTERED' | 'REJECTED';

/** How far the submission has got towards the access point, which is not the same as registration. */
export type ICS2ArrivalDispatchStatus = 'QUEUED' | 'DISPATCHING' | 'SENT' | 'RECONCILIATION_REQUIRED';

export interface ICS2ArrivalSubmitResult {
    arrivalNotificationId: string;
    requestId: string;
    lrn?: string;
    status: ICS2ArrivalStatus;
    dispatchStatus?: ICS2ArrivalDispatchStatus | string;
    transactionId?: string | null;
}

export interface ICS2ArrivalView {
    id: string;
    version: number;
    status: ICS2ArrivalStatus;
    lrn?: string;
    arrivalMrn?: string;
    data?: ICS2ArrivalData;
    submission?: ICS2ArrivalSubmitResult | null;
    houseStateIssues?: boolean;
    controlInstructions?: boolean;
    registeredAt?: string;
    actualArrivalAt?: string;
    sourceManifestId?: string;
}

/** An ENS of the manifest that the service left out of the arrival, with the reason it gave. */
export interface ICS2ArrivalExclusion {
    declarationId: string;
    mrn?: string | null;
    reason: string;
}

export interface ICS2ArrivalCreationResult {
    arrival: ICS2ArrivalView;
    exclusions: ICS2ArrivalExclusion[];
}

export interface ICS2ArrivalNotificationMessage {
    messageUuid: string;
    messageType?: string;
    receivedAt?: string;
    requestId?: string;
    details?: Record<string, unknown>;
    rawMessage?: string;
}

export type ICS2ArrivalHistoryType =
    | 'ARRIVAL_CREATED'
    | 'ARRIVAL_DRAFT_REOPENED'
    | 'ARRIVAL_SUBMITTED'
    | 'ARRIVAL_DELIVERY_CONFIRMED'
    | 'ARRIVAL_DISPATCH_ATTENTION'
    | 'ARRIVAL_DISPATCH_RECOVERED'
    | 'ARRIVAL_REGISTERED'
    | 'ARRIVAL_REJECTED'
    | 'ARRIVAL_INCORRECT_STATE'
    | 'ARRIVAL_CONTROL_INSTRUCTION'
    | 'ARRIVAL_NOTIFICATION_IGNORED'
    | 'ENS_ARRIVAL_CONFIRMED'
    | 'ARRIVAL_CONSULTATION_RESULT';

export type ICS2ArrivalHistoryActor = 'USER' | 'SYSTEM' | 'CUSTOMS' | 'UNKNOWN';

export interface ICS2ArrivalHistoryEntry {
    id: string;
    arrivalId: string;
    type: ICS2ArrivalHistoryType;
    description?: string;
    createdAt?: string;
    createdBy?: string;
    creatorName?: string;
    actorKind?: ICS2ArrivalHistoryActor;
    transactionId?: string;
    requestId?: string;
    messageUuid?: string;
    stateBefore?: string;
    stateAfter?: string;
    context?: Record<string, unknown>;
}

const isEmptyValue = (value: unknown): boolean =>
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value as object).length === 0);

/** Drops empty members, then the object itself once nothing is left in it. */
const compact = <T extends object>(value: T): T | undefined => {
    const entries = Object.entries(value).filter(([, entry]) => !isEmptyValue(entry));
    return entries.length ? (Object.fromEntries(entries) as T) : undefined;
};

const toParty = (party: ICS2ArrivalParty | undefined): ICS2ArrivalParty | undefined => {
    if (!party) return undefined;

    const communication = (party.communication ?? [])
        .map((entry) => compact<ICS2ArrivalCommunication>({ identifier: entry?.identifier, type: entry?.type }))
        .filter((entry): entry is ICS2ArrivalCommunication => !!entry);

    return compact<ICS2ArrivalParty>({ identificationNumber: party.identificationNumber, communication });
};

/**
 * Builds the body of a create or update call out of the form values.
 *
 * The controller decodes the body with `FAIL_ON_UNKNOWN_PROPERTIES` enabled, so a single field the
 * service does not know fails the whole request with a 400. That is why this picks the contract
 * fields by name instead of removing the few known client-only ones: anything the form adds for
 * itself - `manifestId` above all - is left behind by default rather than having to be remembered.
 */
export const toIcs2ArrivalData = (values: ICS2ArrivalData = {}): ICS2ArrivalData => {
    const transport = values.activeBorderTransportMeans;

    return {
        ...compact({
            reference: values.reference,
            activeBorderTransportMeans:
                transport &&
                compact({
                    modeOfTransport: transport.modeOfTransport,
                    identificationNumber: transport.identificationNumber,
                    typeOfIdentification: transport.typeOfIdentification,
                    conveyanceReferenceNumber: transport.conveyanceReferenceNumber,
                    actualDateAndTimeOfArrival: transport.actualDateAndTimeOfArrival,
                    estimatedDateAndTimeOfArrival: transport.estimatedDateAndTimeOfArrival,
                }),
            actualCustomsOfficeOfFirstEntry:
                values.actualCustomsOfficeOfFirstEntry &&
                compact({ referenceNumber: values.actualCustomsOfficeOfFirstEntry.referenceNumber }),
            personNotifyingTheArrival: toParty(values.personNotifyingTheArrival),
            notifyParties: (values.notifyParties ?? [])
                .map(toParty)
                .filter((party): party is ICS2ArrivalParty => !!party),
            relatedMrns: (values.relatedMrns ?? []).map((mrn) => mrn?.trim()).filter(Boolean),
        }),
    };
};
